import DegreeDTO from "features/shared/dtos/DegreeDTO";
import FormGroup from "features/shared/forms/components/FormGroup";
import InputGroup from "features/shared/forms/components/inputGroup/InputGroup";
import PhoneInput from "features/shared/forms/components/phoneInput/PhoneInput";
import { bindMobilePrefix, getPhoneInputPrefixtDefault } from "features/shared/forms/components/phoneInput/phoneInputConfig";
import { UseFormHookResult } from "features/shared/forms/hooks/useForm";
import React, { useEffect } from "react";
import DegreeSelect from "./DegreeSelect";
import { assignDefaultFormValue } from "features/contracting/helpers/contractingFormHelper";
import { getMobileInfoTooltip } from "features/contracting/sections/contractingForms/customerDataForm/customerDataFormHelper";
import { useTranslation } from "react-i18next";

type Props = {
  formHandler: UseFormHookResult;
  objectPrefix: string;
  degrees: DegreeDTO[];
  mobilePhonePrefixRef: React.RefObject<HTMLSelectElement>;
  disabled?: boolean;
  enableMobileTooltip?: boolean;
};

const BusinessPersonSubForm = ({
  formHandler,
  objectPrefix,
  degrees,
  mobilePhonePrefixRef,
  disabled,
  enableMobileTooltip = false
}: Props) => {
  // translations
  const tLabels = useTranslation("contracting\\contracting").t;

  //  form hook props
  const { bindFieldNew, setFormValue, getFormValue, clearFormValues, errors } =
    {
      ...formHandler,
    };

  // side-effects
  useEffect(() => {
    // set default values
    assignDefaultFormValue(
      getInputName("mobilePrefix"),
      getPhoneInputPrefixtDefault(),
      getFormValue,
      setFormValue
    );

    return () => {
      // before unmount component remove values
      clearFormValues([objectPrefix]);
    };
  }, []);

  // helpers
  const getInputName = (name: string) => {
    return `${objectPrefix}.${name}`;
  };

  // render
  return (
    <div className="row no-gutters mx-n2">
      <div className="col-sm-6 px-2">
        <FormGroup
          labelText={tLabels("businessPersonSubForm.firstName.label")}
          inputId={getInputName("firstName")}
          validationError={errors[getInputName("firstName")]}
        >
          <InputGroup
            placement="prepend"
            addonType="select"
            contentElement={
              <DegreeSelect
                name={getInputName("beforeNameDegree")}
                degrees={degrees}
                bindField={bindFieldNew}
                isInFrontOfName
                disabled={disabled}
              />
            }
          >
            <input
              id={getInputName("firstName")}
              className="form-control"
              placeholder={tLabels("businessPersonSubForm.firstName.placeholder")}
              {...bindFieldNew(getInputName("firstName"))}
              disabled={disabled}
            />
          </InputGroup>
        </FormGroup>
      </div>
      <div className="col-sm-6 px-2">
        <FormGroup
          labelText={tLabels("businessPersonSubForm.surname.label")}
          inputId={getInputName("surname")}
          validationError={errors[getInputName("surname")]}
        >
          <InputGroup
            addonType="select"
            contentElement={
              <DegreeSelect
                name={getInputName("afterNameDegree")}
                degrees={degrees}
                bindField={bindFieldNew}
                isInFrontOfName={false}
                disabled={disabled}
              />
            }
          >
            <input
              id={getInputName("surname")}
              className="form-control"
              placeholder={tLabels("businessPersonSubForm.surname.placeholder")}
              {...bindFieldNew(getInputName("surname"))}
              disabled={disabled}
            />
          </InputGroup>
        </FormGroup>
      </div>
      <div className="col-sm-6 px-2">
        <FormGroup
          labelText={tLabels("businessPersonSubForm.email.label")}
          inputId={getInputName("email")}
          validationError={errors[getInputName("email")]}
        >
          <input
            id={getInputName("email")}
            className="form-control"
            placeholder={tLabels("businessPersonSubForm.email.placeholder")}
            {...bindFieldNew(getInputName("email"))}
            disabled={disabled}
          />
        </FormGroup>
      </div>
      <div className="col-sm-6 px-2">
        <FormGroup
          labelText={tLabels("businessPersonSubForm.mobileNumber.label")}
          inputId={getInputName("mobileNumber")}
          validationError={errors[getInputName("mobileNumber")]}
          tooltip={getMobileInfoTooltip(tLabels, enableMobileTooltip)}
        >
          <PhoneInput
            ref={mobilePhonePrefixRef}
            id={getInputName("mobileNumber")}
            {...bindFieldNew(getInputName("mobileNumber"))}
            {...bindMobilePrefix(bindFieldNew, getInputName("mobilePrefix"))}
            placelhoder={tLabels("businessPersonSubForm.mobileNumber.placeholder")}
            disabled={disabled}
          />
        </FormGroup>
      </div>
    </div>
  );
};

export default BusinessPersonSubForm;
